import { isValidPhoneNumber } from "react-phone-number-input";
import iban from "iban";
import * as yup from "yup";

const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[a-zA-Z]{2,})(\/\S*)?$/;
const ibanValidation = (value) => iban.isValid(value);

const addressSchema = yup.object().shape({
  address: yup.string().required("Address is required"),
  city: yup.string(),
  state: yup.string(),
  zip: yup.string(),
  address_title: yup.string().required("Address title is required"),
});

export const projectSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  client: yup.string().required("Client is required"),
  startDate: yup
    .date()
    .required("Start date is required")
    .typeError("Start date must be a valid date"),
  endDate: yup
    .date()
    // .required("End date is required")
    .typeError("End date must be a valid date")
    .nullable(),
  about: yup.string(),
  budget: yup.object().shape({
    value: yup.number().nullable(),
    unit: yup.string(),
  }),
  currency: yup
    .string()
    .oneOf(["USD"], "Currency must be USD")
    .required("Currency is required"),
  emails: yup
    .array()
    .of(yup.string().email("Must be a valid email"))
    .required("Emails are required"),
  pointOfContact: yup.array().of(
    yup.object().shape({
      phone_number: yup
        .string()
        .nullable()
        .test("is-valid-phone", "Please enter correct number", (value) => {
          if (!value || value.trim() === "") {
            return true; // Skip validation if the phone number is empty
          }
          return isValidPhoneNumber(value); // Validate only non-empty phone numbers
        }),

      // .required("Phone number is required"),
      name: yup.string(),
      email: yup.string().email("Must be a valid email"),
    })
  ),
  // code: yup.string(),
  status: yup.string(),
  location: yup.array().of(yup.string()),

  billingAddress: yup.object().shape({
    address: yup.string().required("Address is required"),
    city: yup.string(),
    state: yup.string(),
    zip: yup.string(),
    address_title: yup.string().required("Address title is required"),
  }),

  shippingAddress: yup.object().shape({
    address: yup.string().required("Address is required"),
    city: yup.string(),
    state: yup.string(),
    zip: yup.string(),
    address_title: yup.string().required("Address title is required"),
  }),
  siteAddress: yup.object().shape({
    address: yup.string().required("Address is required"),
    city: yup.string(),
    state: yup.string(),
    zip: yup.string(),
    address_title: yup.string().required("Address title is required"),
  }),
  address: yup.array().of(addressSchema),

  bankTransfers: yup.string(),
  bankMaxSingleTransaction: yup.number(),
  creditCardPayment: yup.string(),
  creditCardMaxSingleTransaction: yup.number(),
  assignees: yup.array().of(yup.string()),
  salesTax: yup.array().of(yup.string()),
  shippingTax: yup.array().of(yup.string()),
});

export const productSchema = yup.object().shape({
  title: yup.string(),
  link: yup.string().test("url", "Please enter a valid URL", (value) => {
    if (!value) return true; // Handle empty case if field is optional

    if (!urlRegex.test(value)) {
      return false;
    }

    // If it passes TLD check, validate as URL
    const urlWithProtocol = value.match(/^https?:\/\//)
      ? value
      : `https://${value}`;
    try {
      new URL(urlWithProtocol);
      return true;
    } catch (err) {
      return false;
    }
  }),
  images: yup
    .array()
    .of(yup.string().required("Image URL is required"))
    .max(5, "Cannot have more than 5 image links"),
  // .required("Image links are required")
  // .min(1, "At least one image link is required"),
  salesCategory: yup.string(),
  category: yup.string(),
  material: yup.string(),
  color: yup.array().of(yup.string()),
  dimension: yup.object().shape({
    length: yup.string(),
    width: yup.string(),
    height: yup.string(),
    // .required("Price is required"),
    unit: yup.string(),
  }),
  otherDimension: yup.string(),
  quantity: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  sku: yup.string(),
  unitPrice: yup.string(),
  totalUnitCost: yup.string(),

  retailPrice: yup.string(),
  totalSellingPrice: yup.string(),
  sellingPrice: yup.string(),
  shippingPrice: yup.string(),
  shippingCost: yup.string(),
  markUp: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  totalMarkUp: yup.string(),
  //sale: yup.number(),
  vendor: yup.string(),
  ref: yup.string(),
  manufacturer: yup.string(),
  description: yup.string(),
  clientDescription: yup.string(),
  vendorDescription: yup.string(),
  tags: yup.array().of(yup.string()),
  notes: yup.string(),
  totalClientSaving: yup.string(),
  shippingMarkup: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  budgetAllowance: yup.string(),
  budgettoSellingPriceComparison: yup.string(),
  taxable: yup.boolean(),
  totalPurchasePrice: yup.string(),
  depositRequest: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  depositReceived: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  depositAmount: yup.string(),

  vendorDiscount: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  totalProfit: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  totalVendorCost: yup.string(),
  totalProfitAmount: yup.string(),
  shipTo: yup.string(),
  sidemark: yup.string(),
  estShip: yup.string(),
  estLeadTime: yup.string(),
  shipped: yup.string().nullable(),
  ordered: yup.number().nullable(),
  estDelivery: yup.string(),
  eta: yup.string(),
  received: yup.string(),
  installScheduled: yup.string(),
  completed: yup.string(),
  carrier: yup.string(),
  tracking: yup.string(),
  shipmentType: yup.string(),
  trackedDeliveryDate: yup.string(),
  availability: yup.string(),
  msrp: yup.string(),
  clientBalanceDue: yup.string(),
  vendorBalanceDue: yup.string(),

  paymentToVendor: yup.string(),
  vendorCost: yup.string(),
  leadTime: yup.string(),
  currency: yup.string(),
  components: yup.array(),
  salesTax: yup.array().of(yup.string()),
  shippingTax: yup.array().of(yup.string()),
});

export const serviceSchema = yup.object().shape({
  title: yup.string(),
  link: yup.string().test("url", "Please enter a valid URL", (value) => {
    if (!value) return true; // Handle empty case if field is optional

    if (!urlRegex.test(value)) {
      return false;
    }

    // If it passes TLD check, validate as URL
    const urlWithProtocol = value.match(/^https?:\/\//)
      ? value
      : `https://${value}`;
    try {
      new URL(urlWithProtocol);
      return true;
    } catch (err) {
      return false;
    }
  }),
  images: yup
    .array()
    .of(yup.string().required("Image URL is required"))
    .max(1, "Cannot have more than 1 image links"),
  // .required("Image links are required")
  // .min(1, "At least one image link is required"),
  salesCategory: yup.string(),
  category: yup.string(),
  quantity: yup.object().shape({
    value: yup.number(),
    unit: yup.string(),
  }),
  unitPrice: yup.string(),
  markUp: yup.object().shape({
    value: yup
      .number()
      .nullable()

      .transform((value, originalValue) =>
        originalValue?.toString()?.trim() === "" ? null : value
      )

      .min(0, "Price must be greater than or equal to 0"),
    // .required("Price is required"),
    unit: yup.string(),
  }),
  vendor: yup.string(),
  clientDescription: yup.string(),
  vendorDescription: yup.string(),
  tags: yup.array().of(yup.string()),
  notes: yup.string(),
  totalClientSaving: yup.string(),
  shippingMarkup: yup.object().shape({
    value: yup.string().nullable(),
    unit: yup.string(),
  }),
  budgetAllowance: yup.string().nullable(),
  budgettoSellingPriceComparison: yup.string().nullable(),
  totalPurchasePrice: yup.string().nullable(),
  depositRequest: yup.object().shape({
    value: yup.string().nullable(),
    unit: yup.string(),
  }),
  depositReceived: yup.object().shape({
    value: yup.string().nullable(),
    unit: yup.string(),
  }),
  depositAmount: yup.string(),
  vendorDiscount: yup.object().shape({
    value: yup.string().nullable(),
    unit: yup.string(),
  }),
  totalProfit: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  totalVendorCost: yup.string(),
  totalProfitAmount: yup.string(),
  shipTo: yup.string(),
  sidemark: yup.string(),
  estShip: yup.string(),
  estLeadTime: yup.string(),
  shipped: yup.string(),
  ordered: yup.string().nullable(),
  estDelivery: yup.string(),
  eta: yup.string(),
  received: yup.string(),
  installScheduled: yup.string(),
  completed: yup.string(),
  carrier: yup.string(),
  tracking: yup.string(),
  shipmentType: yup.string(),
  trackedDeliveryDate: yup.string(),
  totalMarkUp: yup.string(),
  otherDimension: yup.string(),
  totalUnitCost: yup.string(),
  totalSellingPrice: yup.string().nullable(),
  sellingPrice: yup.string(),
  shippingCost: yup.string().nullable(),
  ref: yup.string(),
  location: yup.string(),
  clientNote: yup.string(),
  vendorNote: yup.string(),
  estServiceTimeFrame: yup.string(),
  retailPrice: yup.string().nullable(),
  shippingPrice: yup.string(),
  availability: yup.string(),
  msrp: yup.string(),
  clientBalanceDue: yup.string(),
  vendorBalanceDue: yup.string(),

  paymentToVendor: yup.string(),
  vendorCost: yup.string(),
  leadTime: yup.string(),
  currency: yup.string(),
  salesTax: yup.array().of(yup.string()),
  shippingTax: yup.array().of(yup.string()),
});

export const vendorSchema = yup
  .object()
  .shape({
    name: yup.string(),
    company: yup.string().required("Company Name is required"),
    contact: yup.array().of(
      yup.object().shape({
        phone_number: yup
          .string()
          .nullable()
          .test(
            "is-valid-phone",
            "Please enter a valid phone number",
            (value) => {
              if (!value || value.trim() === "") {
                return true; // Skip validation if the phone number is empty
              }
              return isValidPhoneNumber(value); // Validate only non-empty phone numbers
            }
          ),
        email: yup.string().email("Must be a valid email"),
      })
    ),
    // email: yup.string().email("Invalid email"),
    // phone_numbers: yup.array().of(
    //   yup
    //     .string()
    //     .test(
    //       "is-valid-phone",
    //       "Please enter a valid phone number",
    //       (value) => {
    //         if (!value || value.trim() === "") {
    //           return true; // Skip validation if the phone number is empty
    //         }
    //         return isValidPhoneNumber(value); // Validate only non-empty phone numbers
    //       }
    //     )
    // ),
    website_link: yup
      .string()
      .test("text", "Please enter a valid URL", (value) => {
        if (!value) return true; // Handle empty case if field is optional

        if (!urlRegex.test(value)) {
          return false;
        }

        // If it passes TLD check, validate as URL
        const urlWithProtocol = value.match(/^https?:\/\//)
          ? value
          : `https://${value}`;
        try {
          new URL(urlWithProtocol);
          return true;
        } catch (err) {
          return false;
        }
      }),
    account_number: yup.string(),
    // .test("iban", "Invalid IBAN", (value) =>
    //   value ? ibanValidation(value) : true
    // ),
    address: yup.array().of(
      yup.object({
        address_title: yup.string(), //.required("Address title is required"),
        address: yup.string(), //.required("Address is required"),
        city: yup.string(),
        state: yup.string(),
        zip: yup.string(),
      })
    ),
    vendorDiscount: yup.object().shape({
      value: yup.string().nullable(),
      unit: yup.string(),
    }),
  })
  .test(
    "email-or-phone",
    "Either email or  phone number is required",
    function (values) {
      const { contact } = values;
      const hasEmptyIndex = contact?.some(
        (num) => !num.email && !num.phone_number
      );
      return hasEmptyIndex;
    }
  );

export const clientSchema = yup
  .object()
  .shape({
    name: yup.string().required("Name is required"),
    company: yup.string(),
    // email: yup.string().email("Invalid email").required("Email is required"),
    // phone_numbers: yup.array().of(
    //   yup
    //     .string()
    //     .test("is-valid-phone", "Please enter correct number", (value) => {
    //       if (!value || value.trim() === "") {
    //         return true; // Skip validation if the phone number is empty
    //       }
    //       return isValidPhoneNumber(value); // Validate only non-empty phone numbers
    //     })
    // ),

    contact: yup.array().of(
      yup.object().shape({
        phone_number: yup
          .string()
          .nullable()
          .test(
            "is-valid-phone",
            "Please enter a valid phone number",
            (value) => {
              if (!value || value.trim() === "") {
                return true; // Skip validation if the phone number is empty
              }
              return isValidPhoneNumber(value); // Validate only non-empty phone numbers
            }
          ),
        email: yup.string().email("Must be a valid email"),
      })
    ),

    // address: yup.string(),
    // city: yup.string(),
    // state: yup.string(),
    // zip: yup.string(),
    tags: yup.array().of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      })
    ),
    notes: yup.string(),
    address: yup.array().of(
      yup.object({
        address_title: yup.string().required("Address title is required"),
        address: yup.string().required("Address is required"),
        city: yup.string(),
        state: yup.string(),
        zip: yup.string(),
      })
    ),
  })
  .test(
    "email-or-phone",
    "Either email or  phone number is required",
    function (values) {
      const { contact } = values;
      const hasEmptyIndex = contact?.some(
        (num) => !num.email && !num.phone_number
      );
      return hasEmptyIndex;
    }
  );
// .test(
//   "email-or-phone",
//   "Either email or at least one phone number is required",
//   function (values) {
//     const { email, phone_numbers } = values;
//     const hasPhoneNumber = phone_numbers?.some((num) => num);
//     return !!email || hasPhoneNumber;
//   }
// );

export const memberScehma = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  phone_number: yup
    .string()
    .test("is-valid-phone", "Please enter correct number", (value) => {
      if (!value) return true;
      return isValidPhoneNumber(value || "");
    }),
  role: yup.string().required("Role is required"),
  // permissions: yup.array().of(yup.string()),
});

export const locationSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  budget: yup.object().shape({
    value: yup.string(),
    unit: yup.string(),
  }),
  dimension: yup.object().shape({
    length: yup.string(),
    width: yup.string(),
    height: yup.string(),
    unit: yup.string(),
  }),

  assignee: yup.string(),
});

export const proposalSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  createdOn: yup
    .date()
    .nullable()
    .typeError("Creation date must be a valid date"),
  dueDate: yup.date().nullable().typeError("Due date must be a valid date"),
  client: yup.string().nullable(),
  termsConditions: yup.string().nullable(),
  tags: yup.array().of(yup.string()),
  memo: yup.string().nullable(),
  specifications: yup.array(),
  markup: yup.string().nullable(),
  tax: yup.string().nullable(),
  vendor: yup.string().nullable(),
  salesTax: yup.array().of(yup.string()),
});

export const taskSchema = yup.object().shape({
  title: yup.string().required("Name is required"),
  startTime: yup.mixed().nullable(),
  startDate: yup.date().nullable().typeError("Start date must be a valid date"),
  endDate: yup
    .date()
    .nullable()
    .typeError("End date must be a valid date")
    .when("startDate", {
      is: (startDate: Date | null) => startDate !== null,
      then: (schema) => schema.required("End date is required "),
      otherwise: (schema) => schema.nullable(),
    }),
  endTime: yup.mixed().nullable(),
  assignedTo: yup.string().nullable(),
  location: yup.string().nullable(),
  status: yup.string(),
  description: yup.string().nullable(),
  specifications: yup.array().of(yup.mixed()), // Allow mixed types
  notes: yup.string().nullable(),
  image: yup
    .array()
    .of(yup.mixed().required("Image URL is required"))
    .max(5, "Cannot have more than 5 image links"),
  image_file: yup.array().of(yup.mixed().nullable()),
});

export const accountSettingSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  // personal_info_email: yup.string().email(),
  // company_name: yup.string().required("Company Name is required"),
  // company_website_link: yup.string().matches(urlRegex, "URL is incorrect"),
  // company_email: yup.string().email(),
  // company_phone_number: yup
  //   .string()
  //   .test("is-valid-phone", "Please enter correct number", (value) =>
  //     isValidPhoneNumber(value || "")
  //   ),
  // company_bio: yup.string(),
  // address_line: yup.string(),
  // city: yup.string(),
  // state: yup.string(),
  // zip: yup.string(),
  personal_logo: yup.string(),
  personal_logo_file: yup.mixed().nullable(),
  // company_logo: yup.string(),
  // company_logo_file: yup.mixed().nullable(),
  // address: yup.array().of(
  //   yup.object({
  //     address_title: yup.string().required("Address title is required"),
  //     address_line: yup.string().required("Address is required"),
  //     city: yup.string(),
  //     state: yup.string(),
  //     zip: yup.string(),
  //   })
  // ),
});

export const companyProfileSchema = yup.object().shape({
  company_name: yup.string().required("Company Name is required"),
  company_website_link: yup
    .string()
    .test("url", "Please enter a valid URL", (value) => {
      if (!value) return true; // Handle empty case if field is optional

      if (!urlRegex.test(value)) {
        return false;
      }

      // If it passes TLD check, validate as URL
      const urlWithProtocol = value.match(/^https?:\/\//)
        ? value
        : `https://${value}`;
      try {
        new URL(urlWithProtocol);
        return true;
      } catch (err) {
        return false;
      }
    }),
  company_email: yup.string().email(),
  company_phone_number: yup
    .string()
    .test("is-valid-phone", "Please enter correct number", (value) =>
      isValidPhoneNumber(value || "")
    ),
  company_bio: yup.string(),

  company_logo: yup.string(),
  company_logo_file: yup.mixed().nullable(),
  address: yup.array().of(
    yup.object({
      address_title: yup.string().required("Address title is required"),
      address_line: yup.string().required("Address is required"),
      city: yup.string(),
      state: yup.string(),
      zip: yup.string(),
    })
  ),
});

export const onboardingSchema = yup.object().shape({
  company_name: yup.string().required("Company Name is required"),
  company_website_link: yup
    .string()
    .test("url", "Please enter a valid URL", (value) => {
      if (!value) return true; // Handle empty case if field is optional

      if (!urlRegex.test(value)) {
        return false;
      }

      // If it passes TLD check, validate as URL
      const urlWithProtocol = value.match(/^https?:\/\//)
        ? value
        : `https://${value}`;
      try {
        new URL(urlWithProtocol);
        return true;
      } catch (err) {
        return false;
      }
    }),
  company_phone_number: yup
    .string()
    .test("is-valid-phone", "Please enter correct number", (value) => {
      if (!value || value.trim() === "") {
        return true; // Skip validation if the phone number is empty
      }
      return isValidPhoneNumber(value || "");
    }),
  company_logo: yup.string(),
  company_logo_file: yup.mixed().nullable(),
});

export const addSalestaxSchema = yup.object().shape({
  name: yup.string(),
  // zones: yup.array().of(
  //   yup.object().shape({
  //     name: yup.string()
  //       .required('Zone name is required'),

  //     taxPercentage: yup.number()
  //       .required('Tax percentage is required')
  //       .min(0, 'Tax percentage must be positive')
  //       .max(100, 'Tax percentage cannot exceed 100'),

  //     calculationRule: yup.string()
  //       .required('Calculation rule is required')
  //       .oneOf(
  //         ['per_unit', 'per_total', 'per_invoice'],
  //         'Invalid calculation rule'
  //       ),

  //     appliesTo: yup.object().shape({
  //       products: yup.boolean().default(false),
  //       services: yup.boolean().default(false),
  //       shipping: yup.boolean().default(false),
  //     }),

  //     taxableLimit: yup.object().shape({
  //       amount: yup.number()
  //         .nullable()
  //         .transform((value) => (isNaN(value) ? null : value))
  //         .min(0, 'Taxable limit amount must be positive'),

  //       appliesTo: yup.string()
  //         .oneOf(
  //           ['specification', 'invoice'],
  //           'Invalid taxable limit application'
  //         )
  //         .default('specification'),
  //     }),

  //     taxAuthority: yup.string().required("Tax authority is required"),
  //   })
  // ).min(1, 'At least one zone is required'),
});

export const salesTaxZonesSchema = yup.object().shape({
  name: yup.string().required("Tax Category Name"),
  taxPercentage: yup.number().required("Tax Percentage"),
  calculationRule: yup.string().required("Calculation Rule"),
  appliesTo: yup.array().of(yup.string()),
  taxableLimit: yup.object().shape({
    amount: yup.number(),
    unit: yup.string(),
    appliesTo: yup.string(),
  }),
  taxAuthority: yup.string(),
});

export const requestRetainersSchema = yup.object().shape({
  paymentName: yup.string(),
  originalAmount: yup.string(),
  receivedOn: yup.string(),
  dueDate: yup.string(),
});

export const SaleCategorySchema = yup.object().shape({
  category_name: yup.string().required("Category Name is required"),
  notes: yup.string(),
  sub_categories: yup.array().of(
    yup.object().shape({
      name: yup.string(),
      notes: yup.string(),
    })
  ),
});

export const loginSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
  password: yup.string().required("Password is required"),
});

export const changePasswordSchema = yup.object().shape({
  oldPassword: yup.string().required("Old password is required"),
  newPassword: yup
    .string()
    .required("New password is required")
    .test(
      "not-same-as-old",
      "New password cannot be the same as the old password",
      function (value) {
        return value !== this.parent.oldPassword;
      }
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Confirm password must match new password")
    .required("Confirm password is required"),
});

export const registerSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d).+$/,
      "Password must contain at least one letter and one number"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
});

export const passwordResetSchema = yup.object().shape({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d).+$/,
      "Password must contain at least one letter and one number"
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Confirm Password must match Password")
    .required(),
});
export const emailConfirmSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
});

export const depositSchema = yup.object().shape({
  paymentName: yup.string().required("Name is required"),
  dueDate: yup.string().typeError("Due date must be a valid date"),
  paymentDate: yup.date().nullable().typeError("Due date must be a valid date"),
  paymentMethod: yup.string(),
  originalAmount: yup.string(),
});

export const sendSchema = yup.object().shape({
  to: yup.string().required("Email is required"),
  subject: yup.string().required("Subject is required"),
  messgae: yup.string(),
});
